import React, { useEffect, useState } from "react";
import { getS3URL } from "../../_helpers";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-]+/g, "")
    .replace(/\-\-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
};

const CourseCard = ({ course, onClick, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-3xl p-4 shadow-sm border border-slate-100 flex flex-col justify-between w-full h-[300px] select-none pointer-events-none overflow-hidden relative">
        <div>
          {/* Pulsing Image Placeholder */}
          <div className="relative w-full h-36 bg-slate-200 rounded-2xl mb-4 overflow-hidden">
            <div className="shimmer-sweep" />
          </div>
          {/* Pulsing Partner Name */}
          <div className="h-3 bg-slate-200 rounded w-1/4 mb-2.5 relative overflow-hidden">
            <div className="shimmer-sweep" />
          </div>
          {/* Pulsing Title */}
          <div className="h-5 bg-slate-200 rounded w-5/6 mb-4 relative overflow-hidden">
            <div className="shimmer-sweep" />
          </div>
        </div>
        {/* Pulsing Footer */}
        <div className="pt-3 border-t border-slate-100 flex flex-col gap-1.5">
          <div className="h-3 bg-slate-200 rounded w-1/3 mb-2 relative overflow-hidden">
            <div className="shimmer-sweep" />
          </div>
          <div className="h-4 bg-slate-200 rounded w-1/4 relative overflow-hidden">
            <div className="shimmer-sweep" />
          </div>
        </div>
      </div>
    );
  }

  // Extract partner name dynamically
  const partnerName = course.companyName || 
    (course.CourseName?.toLowerCase().includes('google') ? 'Google' : 
     course.CourseName?.toLowerCase().includes('microsoft') ? 'Microsoft' : 
     course.CourseName?.toLowerCase().includes('ibm') ? 'IBM' : 'SimkLearn');
  
  const partnerLogo = partnerName === 'Google' 
    ? 'https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg'
    : partnerName === 'Microsoft'
    ? 'https://upload.wikimedia.org/wikipedia/commons/9/96/Microsoft_logo_%282012%29.svg'
    : partnerName === 'IBM'
    ? 'https://upload.wikimedia.org/wikipedia/commons/5/51/IBM_logo.svg'
    : null;

  return (
    <div 
      onClick={onClick}
      className="bg-white rounded-3xl p-4 shadow-sm hover:shadow-xl hover:scale-[1.02] border border-slate-100 hover:border-slate-200/60 transition-all duration-100 flex flex-col justify-between w-full h-[300px] cursor-pointer select-none text-gray-800 relative group"
    >
      <div>
        {/* Banner Image wrapper */}
        <div className="relative w-full h-36 rounded-2xl overflow-hidden mb-4 bg-slate-100">
          <img
            src={course?.imageFiles?.[0]?.fileName ? getS3URL(course.imageFiles[0].fileName) : "https://pub-561e50d39a1c4c3a9c5d9a7715790c10.r2.dev/images/Adaptive.jpg"}
            alt={course?.CourseName}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://pub-561e50d39a1c4c3a9c5d9a7715790c10.r2.dev/images/Adaptive.jpg";
            }}
          />
          {/* Vertical white bookmark ribbon overlay containing the partner logo */}
          {partnerLogo && (
            <div className="absolute top-0 left-3.5 bg-white px-2 py-3.5 rounded-b-xl shadow-md flex items-center justify-center w-9 h-11 border border-t-0 border-slate-100/60">
              <img src={partnerLogo} alt={partnerName} className="w-6 h-6 object-contain" />
            </div>
          )}
        </div>

        {/* Brand / Partner name */}
        <span className="text-[10px] uppercase tracking-wider text-slate-400 font-extrabold block mb-1">
          {partnerName}
        </span>

        {/* Title */}
        <h4 className="font-extrabold text-sm text-gray-900 leading-snug line-clamp-2 group-hover:text-indigo-600 transition-colors duration-200">
          {course?.CourseName}
        </h4>
      </div>

      {/* Footer info details */}
      <div className="pt-3.5 border-t border-slate-100 flex flex-col gap-1">
        <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">
          {course?.courseType || 'Professional Certificate'}
        </span>
        <div className="flex items-center gap-1.5 text-xs text-amber-500 font-black mt-0.5">
          <span>★</span>
          <span>{course?.rating || '4.8'}</span>
          <span className="text-slate-400 font-medium">({course?.ID ? (course.ID * 123) % 900 + 100 : '500'})</span>
        </div>
      </div>
    </div>
  );
};

function ExploreProgramHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const { categories, isLoading } = useSelector(state => state?.category || {});
  const { courses, isFetching } = useSelector(state => state?.course || []);
  const courseList = courses?.all?.courses || [];
  
  const [filterCourseData, setFilterCourseData] = useState([]);
  const [activeTab, setActiveTab] = useState();
  const [isCategoryLoading, setIsCategoryLoading] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  useEffect(() => {
    setStartIndex(0);
    if (categories && categories.length > 0) {
      const sortedCats = categories
        ?.slice()
        .sort((a, b) => a.categoryName.localeCompare(b.categoryName));

      const firstCat = sortedCats[0]?.categoryName;
      setActiveTab(firstCat);

      if (courseList && courseList.length > 0) {
        const filtered = courseList.filter((c) => c.category === firstCat);
        setFilterCourseData(filtered);
      }
    }
  }, [categories, courses]);

  const handleTabClick = (program) => {
    if (activeTab === program.categoryName) return;
    setActiveTab(program.categoryName);
    setIsCategoryLoading(true);

    setTimeout(() => {
      const filtered = courseList.filter((c) => c.category === program.categoryName);
      setFilterCourseData(filtered);
      setIsCategoryLoading(false);
    }, 550); // pulsing skeleton loading delay
  };

  const handleViewProgram = (course) => {
    const slug = slugify(course.CourseName);
    navigate(`/${slug}`, { state: { course: course } });
  };

  const sortedCategories = categories
    ?.slice()
    .sort((a, b) => a.categoryName.localeCompare(b.categoryName));

  const ITEMS_PER_PAGE = 5;

  const visibleCategories = sortedCategories?.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const handleNextCategories = () => {
    if (!sortedCategories) return;
    setStartIndex((prev) => {
      const nextIndex = prev + ITEMS_PER_PAGE;
      const maxIndex = sortedCategories.length - ITEMS_PER_PAGE;
      return Math.min(nextIndex, Math.max(0, maxIndex));
    });
  };

  const handlePrevCategories = () => {
    setStartIndex((prev) => {
      return Math.max(0, prev - ITEMS_PER_PAGE);
    });
  };

  const activeCategory = categories?.find(c => c.categoryName === activeTab);
  const activeDescription = activeCategory?.categoryDescription 
    ? activeCategory.categoryDescription.replace(/<[^>]+>/g, "")
    : `Explore professional courses and certifications curated to help you build in-demand ${activeTab || 'industry'} skills.`;

  return (
    <div className="w-full mt-10 px-4 md:px-6">
      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        .shimmer-sweep {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(140, 140, 140, 0.8) 60%,
            transparent 100%
          );
          animation: shimmer 2.0s infinite linear;
        }
        .scrollbar-none::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-none {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      <div className="bg-gradient-to-r from-[#0F172A] via-[#1E3A8A] to-[#2563EB] rounded-[2.5rem] p-6 sm:p-10 md:p-12 lg:p-14 text-white shadow-2xl relative flex flex-col lg:flex-row gap-8 lg:gap-12 items-stretch overflow-hidden">
        
        {/* Left Callout */}
        <div className="lg:w-[25%] flex flex-col justify-between items-start z-10 py-2 min-h-[200px] lg:min-h-0">
          <div className="space-y-4">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight leading-tight">
              Get job-ready for an in-demand career
            </h2>
            <p className={`text-sm sm:text-base text-white/90 font-medium transition-all duration-300 transform ${isCategoryLoading ? "opacity-30 translate-y-0.5" : "opacity-100 translate-y-0"}`}>
              {activeDescription}
            </p>
          </div>
          <button 
            onClick={() => navigate('/search-new')}
            className="mt-6 bg-white hover:bg-white/95 hover:scale-105 active:scale-95 text-blue-600 font-black px-6 py-3.5 rounded-2xl flex items-center gap-2 shadow-lg transition-all duration-300 text-sm"
          >
            <span>Explore programs</span>
            <span>→</span>
          </button>
        </div>

        {/* Right Content */}
        <div className="lg:w-[75%] flex flex-col gap-6 z-10">
          
          {/* Pill Tabs with Navigation Arrows */}
          <div className="flex items-center gap-2 justify-start lg:justify-end select-none w-full lg:w-auto">
            {sortedCategories?.length > ITEMS_PER_PAGE && (
              <button
                onClick={handlePrevCategories}
                disabled={startIndex === 0}
                className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-300 font-extrabold text-sm ${
                  startIndex === 0
                    ? 'border-white/10 bg-white/5 text-white/30 cursor-not-allowed opacity-50'
                    : 'border-white/30 bg-white/10 hover:bg-white/25 text-white cursor-pointer active:scale-95'
                }`}
                aria-label="Previous categories"
              >
                ←
              </button>
            )}

            <div className="flex gap-2.5 items-center overflow-x-auto scrollbar-none py-1">
              {visibleCategories?.map((program) => {
                const isActive = activeTab === program.categoryName;
                return (
                  <button
                    key={program.id}
                    onClick={() => handleTabClick(program)}
                    className={`px-4 py-2 rounded-full text-xs font-bold transition-all duration-300 border whitespace-nowrap ${
                      isActive 
                        ? 'bg-zinc-900 border-zinc-900 text-white shadow-md' 
                        : 'bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-2xl border border-white/20 shadow-2xl rounded-3xl text-white hover:from-white/30 hover:to-white/10 transition-all duration-300'
                    }`}
                  >
                    {program.categoryName}
                  </button>
                );
              })}
            </div>

            {sortedCategories?.length > ITEMS_PER_PAGE && (
              <button
                onClick={handleNextCategories}
                disabled={startIndex + ITEMS_PER_PAGE >= sortedCategories.length}
                className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-300 font-extrabold text-sm ${
                  startIndex + ITEMS_PER_PAGE >= sortedCategories.length
                    ? 'border-white/10 bg-white/5 text-white/30 cursor-not-allowed opacity-50'
                    : 'border-white/30 bg-white/10 hover:bg-white/25 text-white cursor-pointer active:scale-95'
                }`}
                aria-label="Next categories"
              >
                →
              </button>
            )}
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 w-full">
            {(!isLoading && !isFetching && !isCategoryLoading && (!filterCourseData || filterCourseData.length === 0)) ? (
              <div className="col-span-full flex flex-col items-center justify-center p-8 bg-white/10 backdrop-blur-sm rounded-3xl text-center border border-white/10 min-h-[250px]">
                <span className="text-3xl mb-2">🔍</span>
                <p className="text-sm font-semibold">No courses found in this category.</p>
              </div>
            ) : (
              Array.from({ length: 4 }).map((_, i) => {
                const course = filterCourseData?.[i];
                const showSkeleton = isLoading || isFetching || isCategoryLoading || !course;
                return (
                  <CourseCard 
                    key={`card-slot-${i}`}
                    course={course}
                    isLoading={showSkeleton}
                    onClick={() => handleViewProgram(course)}
                  />
                );
              })
            )}
          </div>

        </div>
      </div>
    </div>
  );
}

export default ExploreProgramHome;
